import React from 'react';
import { TitleSize, BaseSize, TextWeight, TextColor } from '@/shared/types/text';
import { cn } from '@/shared/utils/cn';
import { SYSTEM_COLORS } from './constants';

export interface TitleProps {
  /** Tamaño del título: h1-h6 o tamaños base */
  size?: TitleSize | BaseSize;
  /** Peso de la fuente (solo aplica si se usa tamaño base) */
  weight?: TextWeight;
  /** Contenido del título */
  children: React.ReactNode;
  /** Clase CSS adicional */
  className?: string;
  /** Elemento HTML a renderizar (por defecto: h1-h6 según size, o h3 si es tamaño base) */
  as?: keyof JSX.IntrinsicElements;
  /** Color del texto: puede ser un color del sistema de diseño o un color CSS personalizado */
  color?: TextColor | (string & {});
  /** @deprecated Usa 'color' en su lugar */
  textColor?: TextColor;
}

export const Title = ({
  size = 'h3',
  weight = 'regular',
  children,
  className,
  as,
  color,
  textColor,
  ...props
}: TitleProps) => {
  // Determinar el elemento HTML a renderizar
  const getElement = (): keyof JSX.IntrinsicElements => {
    if (as) return as;

    if (size && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(size)) {
      return size as keyof JSX.IntrinsicElements;
    }

    return 'h3';
  };

  const Component = getElement();

  // Construir las clases según el tamaño
  const getTypographyClasses = (): string => {
    const classes: string[] = [];

    if (size && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(size)) {
      // Usar clases individuales de Tailwind que están definidas en fontSize, fontWeight, lineHeight, letterSpacing
      // Usar la misma estructura que el componente Title existente
      classes.push(`ui-text-heading-${size}`);
      classes.push(`ui-font-heading-${size}`);
      classes.push(`ui-leading-tracking-${size}`);
    } else if (size) {
      // Usar valores base (2xs, xs, sm, md, lg, xl, 2xl, etc.)
      classes.push(`ui-text-${size}`);

      // Aplicar weight base
      if (weight !== 'regular') {
        classes.push(`ui-font-${weight}`);
      }
    }

    return classes.join(' ');
  };

  const typographyClasses = getTypographyClasses();

  // Verificar si el color es del sistema de diseño
  const isSystemColor = color && SYSTEM_COLORS.includes(color as TextColor);

  // Estilos inline solo para colores CSS personalizados
  const style = color && !isSystemColor ? { color } : undefined;

  // Color del sistema de diseño o por defecto: primary
  const getSystemColorClass = (): string => {
    // Si es un color del sistema, usar la clase correspondiente
    if (isSystemColor) return `ui-text-${color}`;
    // Si hay color CSS personalizado, no usar clase del sistema
    if (color) return '';
    // textColor deprecated, pero seguir soportándolo
    if (textColor) return `ui-text-${textColor}`;
    return 'ui-text-primary'; // Por defecto
  };

  const systemColorClass = getSystemColorClass();

  return (
    <Component
      className={cn('ui-imj', typographyClasses, systemColorClass, className)}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

Title.displayName = 'Title';
